import React, { createContext, MutableRefObject, PropsWithChildren, useContext } from 'react';
import { DealBuilderContactsData, DealBuilderEquipmentSource, DealBuilderCreditApplicationSource } from '../types';
import { DealBuilderEntity, InventoryEquipmentItem } from '../api';
import { Nullable } from 'global';
import { EquipmentDelivery } from 'modules/equipment/types';
import { CalculatedTotalsByLevel, QuoteCalcViewModel } from 'modules/quote';
import { VendorOpportunity } from 'modules/vendorOpportunity';

export type DeliverySubmitRef = MutableRefObject<
  Nullable<{ submit: () => Promise<{ isValid: boolean; formValues: EquipmentDelivery }> }>
>;

export interface DealBuilderContextValue {
  activeEntity: Nullable<DealBuilderEntity>;
  setActiveEntity: (entity: DealBuilderEntity) => void;
  startNewQuote: () => Promise<Nullable<string>>;
  sourceData: {
    vo?: Nullable<VendorOpportunity>;
    equipments: Nullable<DealBuilderEquipmentSource[]>;
    creditApplication?: Nullable<DealBuilderCreditApplicationSource>;
  };
  contacts: {
    contactsData: DealBuilderContactsData;
  };
  equipment: {
    deliveryAddressSubmitRef: DeliverySubmitRef;
    selectedEquipmentItem: Nullable<InventoryEquipmentItem>;
    setSelectedEquipmentItem: (item: Nullable<InventoryEquipmentItem>) => void;
  };
  pricing: {
    calculatedTotals: CalculatedTotalsByLevel;
    setCalculatedTotals: (totals: CalculatedTotalsByLevel) => void;
    priceCalc: Nullable<QuoteCalcViewModel>;
    isQuoteCalcLoading: boolean;
  };
}

const DealBuilderContext = createContext<DealBuilderContextValue | null>(null);

export const DealBuilderContextProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: DealBuilderContextValue }>) => (
  <DealBuilderContext.Provider value={value}>{children}</DealBuilderContext.Provider>
);

export const useDealBuilderContext = (): DealBuilderContextValue => {
  const context = useContext(DealBuilderContext);
  if (!context) throw new Error('useDealBuilderContext must be used within a DealBuilderContextProvider');
  return context;
};
